// Helpers for the `is_excel` / file-download endpoints.
//
// Those requests are sent with `responseType: "blob"`, so the backend body always
// reaches us as a Blob — including when it is a JSON error payload instead of a
// file. Reading the Blob back is the only way to surface the real message, so all
// export thunks go through `downloadFile` + `getExportErrorMessage`.

export const DEFAULT_EXPORT_ERROR = "حدث خطأ أثناء تصدير الملف";

// Don't try to read a real spreadsheet into memory just to look for an error.
const MAX_INSPECTED_BLOB_SIZE = 512 * 1024;

export class ExportError extends Error {
  constructor(message) {
    super(message || DEFAULT_EXPORT_ERROR);
    this.name = "ExportError";
    this.isExportError = true;
  }
}

const readBlobAsJson = async (blob) => {
  try {
    const text = await blob.text();
    const payload = JSON.parse(text);
    return payload && typeof payload === "object" ? payload : null;
  } catch {
    return null;
  }
};

// Extracts a human readable message out of the shapes the backend uses:
// { errors: [...] } | { errors: "..." } | { error: "..." } | { message: "..." }
const messageFromPayload = (payload) => {
  if (!payload) return null;
  if (typeof payload === "string") return payload.trim() || null;
  const { errors, error, message } = payload;
  if (Array.isArray(errors)) {
    const joined = errors.filter(Boolean).join(" | ");
    if (joined) return joined;
  }
  if (typeof errors === "string" && errors.trim()) return errors;
  if (typeof error === "string" && error.trim()) return error;
  if (typeof message === "string" && message.trim()) return message;
  return null;
};

/**
 * Reads the error message of a failed export, whether the payload arrived as a
 * Blob (blob request), as parsed JSON, or as an ExportError we threw ourselves.
 */
export const getExportErrorMessage = async (
  err,
  fallback = DEFAULT_EXPORT_ERROR,
) => {
  if (err?.isExportError) return err.message || fallback;

  const data = err?.response?.data;
  if (data instanceof Blob) {
    if (data.size > MAX_INSPECTED_BLOB_SIZE) return fallback;
    return messageFromPayload(await readBlobAsJson(data)) ?? fallback;
  }
  return messageFromPayload(data) ?? fallback;
};

/**
 * Saves a blob response as a file. Throws an ExportError carrying the backend
 * message when the response is a JSON error or an empty body instead of a file.
 */
export const downloadFile = async (res, fileName) => {
  const blob = res?.data;
  if (!(blob instanceof Blob)) throw new ExportError(DEFAULT_EXPORT_ERROR);

  // Some endpoints answer a failed export with 200 + a JSON error body.
  if (blob.size <= MAX_INSPECTED_BLOB_SIZE) {
    const payload = await readBlobAsJson(blob);
    if (payload) throw new ExportError(messageFromPayload(payload));
  }
  if (blob.size === 0) throw new ExportError("الملف المصدَّر فارغ");

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};
